/**
 * CAP Reviews — Iteration 21C-C Phase 3 wired page.
 *
 * Lists SUSPENDED institutions with pending CAP submissions and lets a
 * compliance operator approve/reject a plan or reinstate once the
 * 72-hour window has elapsed. Reads the CAP engine endpoints landed
 * in Iteration 21C-A.
 */
import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function CapReviews() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [details, setDetails] = useState(null);
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/institutions");
      const suspended = (r.data?.items || []).filter(
        (i) => i.status === "SUSPENDED" || i.status === "REVOKED"
      );
      setItems(suspended);
      if (suspended.length > 0 && !selected) setSelected(suspended[0].vlk_inst_id);
    } catch (e) {
      toast.error("Could not load institutions");
    }
  }, [selected]);

  const loadDetail = useCallback(async (id) => {
    if (!id) return;
    try {
      const [c, ev] = await Promise.all([
        api.get(`/cap/institution/${id}`),
        api.get(`/cap/institution/${id}/events?limit=50`),
      ]);
      setDetails(c.data);
      setEvents(ev.data?.items || []);
    } catch {
      setDetails(null);
      setEvents([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (selected) loadDetail(selected); }, [selected, loadDetail]);

  const call = async (path, extra = {}) => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.post(`/cap/${path}`, { vlk_inst_id: selected, ...extra });
      toast.success(`CAP ${path} OK`);
      await Promise.all([load(), loadDetail(selected)]);
    } catch (e) {
      toast.error(`CAP ${path} failed`, { description: e?.response?.data?.detail?.code || e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-12 gap-6" data-testid="admin-page-cap">
      <aside className="col-span-4 space-y-2">
        <h1 className="text-lg font-semibold">CAP Reviews</h1>
        <p className="text-xs text-muted-foreground">
          Suspended and revoked institutions across all tenants.
        </p>
        <div className="border rounded divide-y" data-testid="cap-institution-list">
          {items.length === 0 && (
            <div className="p-3 text-xs text-muted-foreground">No suspended institutions.</div>
          )}
          {items.map((i) => (
            <button
              key={i.vlk_inst_id}
              onClick={() => setSelected(i.vlk_inst_id)}
              data-testid={`cap-select-${i.vlk_inst_id}`}
              className={`block w-full text-left p-3 text-sm hover:bg-accent/30 ${
                selected === i.vlk_inst_id ? "bg-accent/40" : ""
              }`}
            >
              <div className="font-medium truncate">{i.name}</div>
              <div className="text-[10px] font-mono text-muted-foreground">{i.vlk_inst_id}</div>
              <div className="text-[10px] uppercase tracking-widest mt-1">
                {i.status}
              </div>
            </button>
          ))}
        </div>
      </aside>

      <section className="col-span-8 space-y-4">
        {!details ? (
          <div className="p-6 border rounded text-sm text-muted-foreground">
            Select an institution on the left.
          </div>
        ) : (
          <>
            <div className="border rounded p-4 space-y-2" data-testid="cap-detail-card">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">
                {details.vlk_inst_id}
              </div>
              <div className="text-lg font-semibold">Status · {details.status}</div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <Kv k="Suspended at" v={details.suspended_at} />
                <Kv k="Earliest reinstatement" v={details.suspension_earliest_reinstatement} />
                <Kv k="CAP deadline" v={details.cap_deadline_at} />
                <Kv k="CAP status" v={details.cap?.status} />
                <Kv k="Reinstated at" v={details.reinstated_at} />
                <Kv k="Revoked at" v={details.revoked_at} />
              </div>
              {details.cap?.plan_text && (
                <div className="mt-2 text-xs">
                  <div className="uppercase tracking-widest text-muted-foreground">Plan</div>
                  <pre className="whitespace-pre-wrap font-mono text-[11px] mt-1 p-2 bg-muted rounded">
                    {details.cap.plan_text}
                  </pre>
                </div>
              )}
              <div className="flex flex-wrap gap-2 pt-2">
                <Button size="sm" disabled={busy || details.cap?.status !== "UNDER_REVIEW"}
                        data-testid="cap-approve-btn"
                        onClick={() => call("approve", { note: "Approved via console" })}>
                  Approve CAP
                </Button>
                <Button size="sm" variant="outline" disabled={busy || details.cap?.status !== "UNDER_REVIEW"}
                        data-testid="cap-reject-btn"
                        onClick={() => call("reject", { note: "Rejected via console" })}>
                  Reject CAP
                </Button>
                <Button size="sm" disabled={busy || details.status !== "SUSPENDED" || details.cap?.status !== "APPROVED"}
                        data-testid="cap-reinstate-btn"
                        onClick={() => call("reinstate", { note: "Reinstated via console" })}>
                  Reinstate
                </Button>
                <Button size="sm" variant="destructive" disabled={busy || details.status === "REVOKED"}
                        data-testid="cap-revoke-btn"
                        onClick={() => call("revoke", { trigger: "REPEATED_TIER2", note: "Manual revocation" })}>
                  Revoke (Tier 3)
                </Button>
              </div>
              {busy && <div className="text-xs text-muted-foreground flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" /> processing…</div>}
            </div>

            <div className="border rounded p-4">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
                Suspension events (append-only)
              </div>
              <div className="space-y-1 max-h-96 overflow-auto" data-testid="cap-events-list">
                {events.length === 0 && (
                  <div className="text-xs text-muted-foreground">No events yet.</div>
                )}
                {events.map((e) => (
                  <div key={e.event_id} className="text-xs border-b pb-1 mb-1 font-mono">
                    <span className="text-muted-foreground">{e.created_at?.slice(0, 19)}</span>
                    <span className="mx-2 uppercase font-semibold">{e.kind}</span>
                    <span className="text-muted-foreground">by {e.actor_email}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function Kv({ k, v }) {
  return (
    <div>
      <div className="uppercase tracking-widest text-[10px] text-muted-foreground">{k}</div>
      <div className="font-mono text-[11px]">{v || "—"}</div>
    </div>
  );
}
